/**
 *
 * @page 企业中心/数据看板/管理员/巡检质量统计
 *
 **/

import dayjs from 'dayjs';
import { useReducer } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useStoreState } from '&/utils/customHooks';
import { observer } from 'mobx-react';
import BasePageWrap from '&/components/ResourceLib/BasePageWrap';
import {
    Button,
    Form,
    Table,
    Spin,
    Space,
    Input,
    message,
    Tooltip,
} from 'antd';
import { Select as AntSelect } from '&/components/AntdEncapsulation';
import {
    getManegers,
    getBacklogList,
    getBacklogDetail,
} from '@/api/personCenter/dataBoard';
import OrganizationSelect from '@/components/UserCenter/OrganizationSelect';
import BaseDrawer from '&/components/BaseDrawer';
import { DAY_OPTIONS, downloadExcel } from './constants';
import { getSelectedManegerLabel, getSelectedDayLabel } from './common';
import './BacklogList.scss';

function BacklogList() {
    const isAdmin = !useRouteMatch({
        path: '/user-center/manager/satisfaction-list',
        strict: false, // 全路径?
        sensitive: true, // 大小写?
    });

    const { entId } = useStoreState('dataBoard');

    const location = useLocation();
    const { search } = location;
    const urlParams = new URLSearchParams(search);
    const initManager = urlParams.get('manager');
    const initDays = urlParams.get('days');

    const [result, setResult] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const dftParams = {
        // page: 1,
        // pageNum: 10,
        days: initDays !== null ? +initDays : 30,
        managerId: initManager,
        isAll: 1,
        export: 0,
    };

    if (isAdmin) dftParams.entId = entId;

    const [queryForm, setQueryForm] = useState(dftParams);

    const [didUpdate, forceUpdate] = useReducer(s => s + 1, 0);

    // 初始化调用和entId更新调用
    useEffect(() => {
        if (isAdmin) {
            setQueryForm({
                ...queryForm,
                entId,
                isAll: !entId ? 1 : 2,
            });

            setDetailParams(prev => ({
                ...prev,
                entId,
                isAll: !entId ? 1 : 2,
            }));
        }
        forceUpdate();
    }, [entId]);

    useEffect(() => {
        queryManagerOptions(queryForm);
    }, [queryForm.entId]);

    const onParamChange = (val, key) => {
        setQueryForm(prev => ({ ...prev, [key]: val, page: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const getExcelFileName = () => {
        return (
            '待办异常统计' +
            '-' +
            getSelectedManegerLabel(managerOpts, queryForm.managerId) +
            '-' +
            getSelectedDayLabel(DAY_OPTIONS(intl), queryForm.days) +
            '-' +
            dayjs().format('YYYY_MM_DD')
        );
    };

    const queryList = async () => {
        const PAYLOAD = { ...queryForm };
        !PAYLOAD.entId && delete PAYLOAD.entId;
        const { export: isExport } = PAYLOAD;
        try {
            !isExport && setIsLoading(true);
            const res = await getBacklogList(PAYLOAD);
            if (isExport) {
                downloadExcel(res, getExcelFileName()).catch(err => {
                    if (err.code === 0) {
                        message.warn('无内容');
                    } else {
                        message.warn(err?.msg);
                    }
                });
            } else {
                const { data } = res;
                const list = (data || []).map(item => {
                    item.stageName = item['_id'].stageName;
                    return item;
                });

                setResult(list);
            }
        } finally {
            setIsLoading(false);
            isExport && setQueryForm(prev => ({ ...prev, export: 0 }));
        }
    };

    useEffect(() => {
        didUpdate > 0 && queryList();
    }, [didUpdate]);

    const dftManeger = {
        value: null,
        label: intl.get('base.all'),
    };
    const [managerOpts, setManagerOpts] = useState([dftManeger]);

    function queryManagerOptions(payload) {
        const PARAMS = {
            isAll: payload.isAll,
            roleId: 2, // 项目经理
        };

        if (payload.entId) {
            PARAMS.entId = payload.entId;
        }
        getManegers(PARAMS).then(({ data }) => {
            const opts = [dftManeger];
            for (const el of data.list) {
                opts.push({
                    value: el.userId,
                    label: el.username,
                });
            }
            setManagerOpts(opts);
        });
    }

    const [visible, setVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailParams, setDetailParams] = useState({
        isAll: 1,
        managerId: null,
        export: 0,
        projectStatus: null,
        projectName: null,
    });

    const [didUpdate1, forceUpdate1] = useReducer(s => s + 1, 0);

    useEffect(() => {
        !visible && initParams();
    }, [visible]);

    function initParams() {
        setDetailParams(prev => ({
            ...prev,
            managerId: null,
            export: 0,
            projectStatus: null,
            projectName: null,
        }));
    }

    useEffect(() => {
        visible && queryDetail();
    }, [didUpdate1]);

    const onDetailParamsChange = (val, key) => {
        setDetailParams(prev => ({
            ...prev,
            [key]: val,
        }));
        key !== 'projectName' && forceUpdate1();
    };

    const [detaileTitle, setDetaileTitle] = useState('');

    const showDetail = ({ stageName }) => {
        setVisible(true);
        setDetailParams(prev => ({
            ...prev,
            stageName,
            days: queryForm.days,
        }));
        setDetaileTitle(`${stageName}—待办异常统计`);
        forceUpdate1();
    };

    const onExoprtDetail = () => {
        setDetailParams(prev => ({ ...prev, export: 1 }));
        forceUpdate1(); // 防止输入时不停触发搜索
    };

    async function queryDetail() {
        const PARAMS = { ...detailParams };
        const { export: isExport } = PARAMS;
        if (!PARAMS.entId) delete PARAMS.entId;
        !isExport && setDetailLoading(true);
        try {
            const res = await getBacklogDetail(PARAMS);
            if (isExport) {
                downloadExcel(res, detaileTitle).catch(err => {
                    if (err.code === 0) {
                        message.warn('无内容');
                    } else {
                        message.warn(err?.msg);
                    }
                });
            } else {
                setDetailData(res.data);
            }
        } catch (error) {
        } finally {
            setDetailLoading(false);
            isExport && setDetailParams(prev => ({ ...prev, export: 0 }));
        }
    }

    const onClose = () => {
        setVisible(false);
    };

    const { Item } = Form;

    const subColumns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 60,
            align: 'center',
            render: (_, __, idx) => {
                return idx + 1;
            },
        },
        {
            title: '项目名称',
            width: 100,
            dataIndex: 'projectName',
        },
        {
            title: '延期未完成',
            width: 90,
            dataIndex: 'delayNotFinish',
        },
        {
            title: '延期完成',
            width: 90,
            dataIndex: 'delayFinish',
        },
        {
            title: '项目状态',
            dataIndex: 'projectStatus',
        },
        {
            title: '项目经理',
            dataIndex: 'manager',
            align: 'center',
            width: 100,
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '手机号码',
            dataIndex: 'managerMobile',
            align: 'center',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
    ];

    const statusList = [
        {
            label: '全部',
            value: null,
        },
        {
            label: '未开始',
            value: 0,
        },
        {
            label: '施工中',
            value: 1,
        },
        {
            label: '待验收',
            value: 2,
        },
        {
            label: '验收通过',
            value: 3,
        },
        {
            label: '已延期',
            value: 4,
        },
    ];

    const detailModalEl = (
        <BaseDrawer
            visible={visible}
            title={
                <>
                    {detaileTitle}
                    <Button
                        type="primary"
                        onClick={onExoprtDetail}
                        disabled={!detailData.length}>
                        导出表单
                    </Button>
                </>
            }
            destroyOnClose
            bodyStyle={{
                padding: '1.375rem 1.25rem',
            }}
            className="inspection-item-detail"
            onClose={onClose}>
            <Spin spinning={detailLoading}>
                <Space wrap size={[15, 0]}>
                    <Item label="项目名称">
                        <Input.Search
                            allowClear
                            onSearch={forceUpdate1}
                            style={{
                                width: '10rem',
                            }}
                            value={detailParams.projectName}
                            placeholder="输入项目名称"
                            onChange={e =>
                                onDetailParamsChange(
                                    e.target.value,
                                    'projectName'
                                )
                            }></Input.Search>
                    </Item>
                    <Item label={'项目经理'}>
                        <AntSelect
                            style={{ width: '8rem' }}
                            onChange={e => onDetailParamsChange(e, 'managerId')}
                            className="manager-select"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={managerOpts}
                            value={detailParams.managerId}
                        />
                    </Item>
                    <Item label="项目状态">
                        <AntSelect
                            style={{ width: '8rem' }}
                            options={statusList}
                            value={detailParams.projectStatus}
                            placeholder={'请选择'}
                            onChange={e =>
                                onDetailParamsChange(e, 'projectStatus')
                            }
                        />
                    </Item>
                    <Item label={'时间区间'}>
                        <AntSelect
                            style={{ width: '8rem' }}
                            value={detailParams.days}
                            onChange={e => onDetailParamsChange(e, 'days')}
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </Space>

                <Table
                    className="normal-table"
                    bordered
                    columns={subColumns}
                    dataSource={detailData}
                    size="small"
                    scroll={{
                        y: 'calc(100vh - 12.4rem)',
                    }}
                    pagination={false}
                />
            </Spin>
        </BaseDrawer>
    );

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 70,
            align: 'center',
            render: (_, __, idx) => {
                return (
                    idx + 1 /*  + (queryForm.page - 1) * queryForm.pageNum */
                );
            },
        },
        {
            title: '施工阶段',
            dataIndex: 'stageName',
            render: (stageName, row) => {
                return (
                    <span
                        className="native-link pointer"
                        onClick={() => showDetail(row)}>
                        {stageName}
                    </span>
                );
            },
        },
        {
            title: '延期未完成',
            dataIndex: 'delayNotFinish',
            sorter: {
                compare: (a, b) => a.delayNotFinish - b.delayNotFinish,
            },
        },
        {
            title: '延期完成',
            dataIndex: 'delayFinish',
            sorter: {
                compare: (a, b) => a.delayFinish - b.delayFinish,
            },
        },
        {
            title: '项目经理',
            dataIndex: 'manager',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '更新时间',
            dataIndex: 'checkDate',
            align: 'center',
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];

    const onExoprt = () => {
        setQueryForm(prev => ({ ...prev, export: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const addBtn = (
        <Button type="primary" onClick={onExoprt} disabled={!result.length}>
            导出表单
        </Button>
    );

    const [page, setPage] = useState(1);
    return (
        <>
            {isAdmin && <OrganizationSelect />}
            <BasePageWrap
                title={<strong>待办异常统计</strong>}
                className="inspection-list"
                type="card"
                headerRight={addBtn}>
                <header className="member-list-form">
                    <Item label={'项目经理'}>
                        <AntSelect
                            onChange={e => onParamChange(e, 'managerId')}
                            className="manager-select"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={managerOpts}
                            value={queryForm.managerId}
                        />
                    </Item>
                    <Item label={'时间区间'}>
                        <AntSelect
                            value={queryForm.days}
                            onChange={e => onParamChange(e, 'days')}
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </header>

                <Table
                    className="normal-table"
                    loading={isLoading}
                    bordered
                    columns={columns}
                    dataSource={result}
                    size="small"
                    pagination={{
                        size: 'small',
                        current: page,
                        onChange: setPage,
                        defaultPageSize: 15,
                        total: result.length,
                        showSizeChanger: false,
                        style: { marginBottom: 0 },
                        showTotal: total => intl.get('base.total', { total }),
                    }}
                />
            </BasePageWrap>
            {detailModalEl}
        </>
    );
}

export default observer(BacklogList);
